import { ApolloError } from "@apollo/client";
import React, {
    ReactElement, useContext, useEffect,
    useMemo, useState,
} from "react";
import { subscribeError, unsubscribeError } from "../api";
import { Loading } from "../components";
import { ErrorDefault } from "../pages/error/Error";
import {
    Permission, Session, useGetActiveSessionsLazyQuery, useGetActiveSessionsQuery, useLogoutMutation, User,
} from "../__generated__/api";
import usePortal from "./PortalContext";
import useRouting from "./Routing";
import useLogging from "./logging/LoggingContext";
import { SelectionState } from "../pages/agent/selection-state";

const REFRESH_INTERVAL = 1000 * 60 * 60;

const USER_KEY = "user";

export interface AuthenticationContext {
    isLoggedIn: boolean;
    userId?: string;
    user?: User & {email: string};
    current?: Session;
    permissions: Set<Permission>;
    logout: () => void;
    invalidate: () => void;
    clearCache: () => void;
}

interface CachedUser {
    isLoggedIn: boolean;
    session: Session;
    user: User & {email: string};
    permissions: Permission[];
    lastUpdated: number;
}

export const AuthContext = React.createContext<AuthenticationContext>({
    isLoggedIn: false,
    userId: "",
    user: undefined,
    current: undefined,
    permissions: new Set(),
    logout: () => {
        // logout
    },
    invalidate: () => {
        // logout
    },
    clearCache: () => {
        // logout
    },
});

export function AuthProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const { redirect, Paths } = useRouting();
    const { invalidate: invalidatePortal } = usePortal();

    const {
        loading, data, error, refetch,
    } = useGetActiveSessionsQuery();
    const [logout] = useLogoutMutation();

    const logging = useLogging();

    const session = data?.session;
    const user = session?.user;

    const clearCache = () => {
        localStorage.removeItem(USER_KEY);
    };

    const invalidate = () => {
        clearCache();
        refetch();
        invalidatePortal();
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            // ok. Do a local logout.
        }
        invalidate();
        invalidatePortal();
        redirect(Paths.DEFAULT);
    };

    useEffect(() => {
        const handler = () => {
            invalidate();
        };
        subscribeError(handler);
        return () => {
            unsubscribeError(handler);
        };
    }, []);

    const isNotLoggedIn = (!loading && data?.session == null) || error?.message?.startsWith("Not authorized");
    const notLoggedIn = !isNotLoggedIn ? undefined : {
        isLoggedIn: false,
        permissions: new Set<Permission>(),
        userId: "",
        user: undefined,
        current: undefined,
        logout: handleLogout,
        invalidate,
        clearCache,
    };

    const userCached = localStorage.getItem(USER_KEY);

    const state: AuthenticationContext | undefined = useMemo(() => {
        let current: AuthenticationContext | undefined;
        if (session && user) {
            current = {
                current: session as Session,
                user: user as User & {email: string},
                isLoggedIn: user != null,
                userId: user?.id,
                permissions: new Set<Permission>(session?.permissions as Permission[] ?? []),
                logout: handleLogout,
                invalidate,
                clearCache,
            };
        }

        if (current) {
            localStorage.setItem(USER_KEY, JSON.stringify({
                isLoggedIn: current.isLoggedIn,
                lastUpdated: Date.now(),
                permissions: Array.from(current.permissions),
                session: current.current,
                user: current.user,
            } as CachedUser));
        } else if (userCached) {
            const userRaw = JSON.parse(userCached) as CachedUser;
            if (Date.now() - (userRaw.lastUpdated ?? 0) < REFRESH_INTERVAL) {
                current = {
                    isLoggedIn: userRaw.isLoggedIn,
                    permissions: new Set(userRaw.permissions),
                    current: userRaw.session,
                    user: userRaw.user,
                    userId: userRaw.user.id,
                    logout: handleLogout,
                    invalidate,
                    clearCache,
                };
            }
        }

        return current;
    }, [session, user, userCached != null]);

    useEffect(() => {
        if (notLoggedIn) {
            logging.info({ }, "Started Unauthenticated session");
        } else if (state) {
            logging.info({ userId: state.userId }, "Started Authenticated Session");
        }
    }, [state?.user, notLoggedIn]);

    if (loading) {
        return <Loading />;
    }

    if (notLoggedIn) {
        return (
            <AuthContext.Provider value={notLoggedIn}>
                {children}
            </AuthContext.Provider>
        );
    }

    if (!state) {
        return <Loading />;
    }

    if (error) {
        return <ErrorDefault message={error instanceof ApolloError ? error.message : "Unknown error occured."} />;
    }

    return (
        <AuthContext.Provider value={state}>
            {children}
        </AuthContext.Provider>
    );
}

export default function useCurrentSession(): AuthenticationContext {
    return useContext(AuthContext) ?? {};
}
